import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import { Spinner, Alert, Modal, StatusBadge, Pagination, EmptyState } from '../../components/UI';
import { useConfirm } from '../../components/ConfirmProvider';

const emptyOptions = ['A', 'B', 'C', 'D'].map(l => ({ option_label: l, option_text: '', is_correct: false }));

function emptyForm() {
  return {
    department_id: '', question_type: 'mcq', question_text: '',
    max_score: 1, validate_type: 'system', correct_answer: '', explanation: '',
    options: emptyOptions.map(o => ({ ...o })),
    paper_id: '',
  };
}

export default function Questions() {
  const confirm = useConfirm();
  const [departments, setDepartments] = useState([]);
  const [papers, setPapers] = useState([]);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ department_id: '', type: '', search: '' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [modal, setModal] = useState(null);
  const [modalError, setModalError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.department_id) params.set('department_id', filters.department_id);
      if (filters.type) params.set('type', filters.type);
      if (filters.search) params.set('search', filters.search);
      setData(await api.get(`/questions?${params}`));
    } catch (e) { setError(e.message); }
  }, [page, filters]);

  const loadPapers = useCallback(() => {
    api.get('/papers').then(setPapers).catch(e => setError(e.message));
  }, []);

  useEffect(() => { api.get('/departments').then(setDepartments).catch(e => setError(e.message)); }, []);
  useEffect(() => { loadPapers(); }, [loadPapers]);
  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setModalError('');
    setNotice('');
    loadPapers();            // statuses may have changed since the page was opened
    setModal(emptyForm());
  }

  function openEdit(q) {
    setModalError('');
    setModal({
      id: q.id, department_id: q.department_id, question_type: q.question_type,
      question_text: q.question_text, max_score: q.max_score, validate_type: q.validate_type,
      correct_answer: q.correct_answer || '', explanation: q.explanation || '',
      options: q.question_type === 'mcq' && q.options.length
        ? q.options
        : emptyOptions.map(o => ({ ...o })),
    });
  }

  function updateOption(i, patch) {
    setModal(prev => {
      const options = prev.options.map((o, idx) => idx === i ? { ...o, ...patch } : (patch.is_correct ? { ...o, is_correct: false } : o));
      return { ...prev, options };
    });
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setModalError('');
    setNotice('');
    try {
      const payload = { ...modal };
      if (payload.question_type !== 'mcq') {
        payload.options = [];
      } else {
        payload.validate_type = 'system';
        payload.options = payload.options.filter(o => o.option_text.trim());
      }
      if (modal.id) {
        await api.put(`/questions/${modal.id}`, payload);
        setNotice('Question updated.');
      } else {
        // A rejected paper (live / already stopped) fails the whole request, so
        // nothing is saved and the form stays open for another paper to be picked.
        const saved = await api.post('/questions', payload);
        setNotice(saved.added_to_paper
          ? `Question saved and added to "${saved.added_to_paper.title}".`
          : 'Question saved to the question bank.');
      }
      setModal(null);
      loadPapers();
      await load();
    } catch (e) { setModalError(e.message); }
    setSaving(false);
  }

  async function remove(q) {
    if (!(await confirm({ title: 'Delete question', message: 'Delete this question? This cannot be undone.', confirmText: 'Delete', danger: true }))) return;
    try {
      const res = await api.del(`/questions/${q.id}`);
      if (res.message) window.alert(res.message);
      await load();
    } catch (e) { setError(e.message); }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="eyebrow">Exam setup</div>
          <h1>Question bank</h1>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>Add question</button>
      </div>

      <Alert type="danger">{error}</Alert>
      <Alert type="success">{notice}</Alert>

      <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <div className="form-row" style={{ marginBottom: 0 }}>
          <div className="form-group" style={{ minWidth: 200 }}>
            <label>Search</label>
            <input type="text" placeholder="Search question text" value={filters.search}
              onChange={e => { setPage(1); setFilters(f => ({ ...f, search: e.target.value })); }} />
          </div>
          <div className="form-group">
            <label>Department</label>
            <select value={filters.department_id} onChange={e => { setPage(1); setFilters(f => ({ ...f, department_id: e.target.value })); }}>
              <option value="">All departments</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Type</label>
            <select value={filters.type} onChange={e => { setPage(1); setFilters(f => ({ ...f, type: e.target.value })); }}>
              <option value="">All types</option>
              <option value="mcq">MCQ</option>
              <option value="fill_blank">Fill in the blank</option>
              <option value="short_answer">Short answer</option>
            </select>
          </div>
        </div>
      </div>

      {!data && !error && <Spinner />}

      {data && data.questions.length === 0 && (
        <div className="card"><EmptyState title="No questions found" sub="Try adjusting filters, or add a new question." /></div>
      )}

      {data && data.questions.length > 0 && (
        <>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Question</th><th>Dept</th><th>Type</th><th>Validate</th><th>Marks</th><th></th></tr></thead>
              <tbody>
                {data.questions.map(q => (
                  <tr key={q.id}>
                    <td style={{ maxWidth: 380 }}>{q.question_text}</td>
                    <td className="text-sm text-muted">{q.department_name}</td>
                    <td><StatusBadge status={q.question_type.replace('_', ' ')} /></td>
                    <td><StatusBadge status={q.validate_type} /></td>
                    <td className="mono">{q.max_score}</td>
                    <td className="table-actions">
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(q)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => remove(q)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {modal && (
        <Modal title={modal.id ? 'Edit question' : 'Add question'} onClose={() => setModal(null)} wide>
          <form onSubmit={save}>
            <Alert type="danger">{modalError}</Alert>

            {!modal.id && (
              <div className="form-group">
                <label>Add to question paper (optional)</label>
                <select value={modal.paper_id} onChange={e => setModal({ ...modal, paper_id: e.target.value })}>
                  <option value="">Question bank only &mdash; do not add to a paper</option>
                  {papers.map(p => (
                    <option key={p.id} value={p.id} disabled={p.status === 'active'}>
                      {p.title} &mdash; {p.status} ({p.question_count} questions)
                      {p.status === 'active' ? ' — live, locked' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-muted mt-2">
                  Any paper except a live one can take new questions &mdash; stop a live paper first.
                  Attempts already recorded keep the questions they were sat with; a question added now
                  only shows up in attempts started after this.
                </p>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Department</label>
                <select value={modal.department_id} onChange={e => setModal({ ...modal, department_id: e.target.value })} required>
                  <option value="">Select department</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Question type</label>
                <select
                  value={modal.question_type}
                  onChange={e => setModal({ ...modal, question_type: e.target.value, validate_type: e.target.value === 'mcq' ? 'system' : modal.validate_type })}
                >
                  <option value="mcq">MCQ (options)</option>
                  <option value="fill_blank">Fill in the blank</option>
                  <option value="short_answer">Short answer</option>
                </select>
              </div>
              <div className="form-group">
                <label>Max score</label>
                <input type="number" min={0.5} step={0.5} value={modal.max_score} onChange={e => setModal({ ...modal, max_score: e.target.value })} required />
              </div>
            </div>

            <div className="form-group">
              <label>Question text</label>
              <textarea value={modal.question_text} onChange={e => setModal({ ...modal, question_text: e.target.value })} required rows={3} />
            </div>

            {modal.question_type === 'mcq' ? (
              <div className="form-group">
                <label>Options &mdash; mark the correct one</label>
                {modal.options.map((o, i) => (
                  <div key={o.option_label} className="flex-gap mt-2">
                    <span className="option-label" style={{ flexShrink: 0 }}>{o.option_label}</span>
                    <input
                      type="text" placeholder={`Option ${o.option_label} text`} value={o.option_text}
                      onChange={e => updateOption(i, { option_text: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <label className="checkbox-row" style={{ marginBottom: 0, flexShrink: 0 }}>
                      <input type="radio" name="correct-option" checked={o.is_correct} onChange={() => updateOption(i, { is_correct: true })} />
                      Correct
                    </label>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Validate type</label>
                  <select value={modal.validate_type} onChange={e => setModal({ ...modal, validate_type: e.target.value })}>
                    <option value="system">System &mdash; exact/keyword match against reference answer</option>
                    <option value="ai">AI &mdash; model scores the candidate's free-text answer</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{modal.validate_type === 'ai' ? 'Reference answer (guides the AI scorer)' : 'Correct answer'}</label>
                  <textarea
                    value={modal.correct_answer} onChange={e => setModal({ ...modal, correct_answer: e.target.value })}
                    required={modal.validate_type === 'system'} rows={2}
                    placeholder="Use | to separate accepted alternatives, e.g. Dr Rao|Dr. Rao"
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label>Explanation (optional, admin-facing)</label>
              <textarea value={modal.explanation} onChange={e => setModal({ ...modal, explanation: e.target.value })} rows={2} />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save question'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
